import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { useDocMeta } from "../../lib/useDocMeta";
import { track, EVENT } from "../../lib/publicAnalytics";

const STEPS = [
  {
    title: "Build or import a prospect",
    body: "Drop in a company by name, NAICS, or UEI — or upload a target list. No real CRM data is touched in the public tour.",
    visual: "Synthetic CRM Card · ZZTEST_AcmeFed · 22 employees · SDVOSB · NAICS 541512",
  },
  {
    title: "Generate an AI Intelligence Report",
    body: "Claude Sonnet 4.5 produces a 100-point report in under a minute — factors, reasons, risks, and a recommended action.",
    visual: "Generating · ETA 32s · Factors: 5 · Bands: Immediate / This Week / This Month / Nurture",
  },
  {
    title: "Review fit, risks, and competitive signals",
    body: "See Top 5 reasons to pursue, Top 3 risks, incumbent indicators, and recompete probability.",
    visual: "Reasons (5) · Risks (3) · Incumbent indicators (2) · Recompete: HIGH",
  },
  {
    title: "Score the prospect or opportunity",
    body: "A pinned formula — federal pursuit + small business + pain + accessibility + buyer likelihood = a 100-point score. Deterministic.",
    visual: "Federal Pursuit 28/30 · SB 15/15 · Pain 19/20 · Accessibility 10/10 · Buyer 24/25",
  },
  {
    title: "Identify recommended next actions",
    body: "An outreach angle, a concrete next action, and a recommended decision maker — drawn from the report's evidence.",
    visual: "Next: Send case study to CEO · Channel: Email · Timing: This week",
  },
  {
    title: "Use insights for capture, teaming, bid/no-bid, and proposals",
    body: "Bid with confidence. Team with partners who fill the gap. Walk away when the math says walk away.",
    visual: "Recommendation: BID WITH CAUTION · Decision audit trail captured",
  },
  {
    title: "Save results inside the customer platform",
    body: "When you become a customer (V2 Phase 3, coming soon), every analysis lives in your dashboard with a private share link.",
    visual: "Customer dashboard preview · Saved analyses · Share links · Customer AI Copilot",
  },
];

export default function Tour() {
  const [i, setI] = useState(0);

  useDocMeta({
    title: "Product Tour · GovBidIQ",
    description: "A 3-minute guided tour of how GovBidIQ produces 100-point Opportunity Intelligence Reports.",
    path: "/tour",
  });

  useEffect(() => { track(EVENT.PRODUCT_TOUR_VIEWED, {}); }, []);
  useEffect(() => { if (i === 0) track(EVENT.PRODUCT_TOUR_STARTED, {}); }, [i]);
  useEffect(() => { if (i === STEPS.length - 1) track(EVENT.PRODUCT_TOUR_COMPLETED, {}); }, [i]);

  const s = STEPS[i];
  const last = i === STEPS.length - 1;

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-14">
      <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ Product Tour</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl text-slate-100 leading-tight mb-2">Three minutes. Seven steps. One decision.</h1>
      <p className="text-slate-400 text-lg max-w-2xl leading-relaxed mb-8">
        This is the same flow customers use every day — with synthetic data so we can show it publicly.
      </p>

      {/* progress */}
      <div className="flex items-center gap-1.5 mb-6" role="progressbar" aria-valuenow={i + 1} aria-valuemin={1} aria-valuemax={STEPS.length}>
        {STEPS.map((_, idx) => (
          <div key={idx} className={`h-1 flex-1 ${idx <= i ? "bg-sky-500" : "bg-slate-800"} transition-colors`} />
        ))}
      </div>

      <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-6 sm:p-10 mb-6 min-h-[340px]" data-testid={`tour-step-${i + 1}`}>
        <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">Step {i + 1} of {STEPS.length}</div>
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-100 mb-3">{s.title}</h2>
        <p className="text-slate-300 text-base leading-relaxed mb-6 max-w-2xl">{s.body}</p>
        <div className="border border-sky-500/30 bg-slate-950/50 p-4 font-mono text-xs text-sky-200 leading-relaxed">
          <div className="text-[10px] tracking-widest uppercase text-sky-400 mb-1.5">/ Synthetic Preview</div>
          {s.visual}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => setI((n) => Math.max(0, n - 1))} disabled={i === 0} data-testid="tour-prev" className="inline-flex items-center gap-2 border border-slate-700 hover:border-slate-500 text-slate-300 font-mono text-[10px] tracking-widest uppercase px-4 py-2.5 disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronLeft className="w-3.5 h-3.5" /> Previous
        </button>
        <div className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">{i + 1} / {STEPS.length}</div>
        {!last ? (
          <button onClick={() => setI((n) => Math.min(STEPS.length - 1, n + 1))} data-testid="tour-next" className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Link to="/discovery" data-testid="tour-finish-discovery" className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Start AI Discovery
            </Link>
            <Link to="/sample" data-testid="tour-finish-sample" className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
              See a Sample Report
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
